import React, { useState } from 'react';
import { X, Download } from 'lucide-react';
import { genericInvoiceAPI } from '../../services/api';
import { toast } from 'react-toastify';

const InvoicePreviewModal = ({ invoice, onClose }) => {
  const [isOpen, setIsOpen] = useState(false);

  React.useEffect(() => {
    setIsOpen(true);
  }, []);

  const handleDownloadPdf = async () => {
    try {
      const response = await genericInvoiceAPI.getInvoicePdf(invoice.id);
      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${invoice.invoiceNumber || invoice.id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setTimeout(onClose, 300);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const dueDate = invoice.invoiceDate ? (() => {
    const d = new Date(invoice.invoiceDate);
    d.setDate(d.getDate() + (invoice.paymentTermsDays || 30));
    return d;
  })() : null;

  const formatCurrency = (amount) => {
    if (!amount) return '₹0.00';
    return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-end z-50">
      <div 
        className={`bg-white h-full w-full max-w-4xl shadow-xl flex flex-col transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Invoice Preview</h2>
            <p className="text-sm text-gray-500 mt-1">{invoice.invoiceNumber}</p>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-3xl mx-auto bg-white border border-gray-200 rounded-lg p-8 space-y-6">
            {/* Invoice Header */}
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">INVOICE</h1>
                <p className="text-sm text-gray-600 mt-2">#{invoice.invoiceNumber}</p>
                <p className="text-sm text-gray-600">Date: {formatDate(invoice.invoiceDate)}</p>
                {invoice.placeOfSupply && (
                  <p className="text-sm text-gray-600">Place of Supply: {invoice.placeOfSupply}</p>
                )}
                {invoice.customerPO && (
                  <p className="text-sm text-gray-600">PO: {invoice.customerPO}</p>
                )}
              </div>
              <div className="text-right">
                <h2 className="text-lg font-semibold">{invoice.legalBusinessName}</h2>
                {invoice.gstNumber && <p className="text-sm text-gray-600">GST: {invoice.gstNumber}</p>}
                {invoice.streetLine1 && (
                  <div className="text-sm text-gray-600 mt-2">
                    <p>{invoice.streetLine1}</p>
                    {invoice.streetLine2 && <p>{invoice.streetLine2}</p>}
                    <p>{invoice.city}, {invoice.state} {invoice.zipcode}</p>
                    <p>{invoice.country}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Bill To */}
            <div className="border-t border-b border-gray-200 py-4">
              <h3 className="text-sm font-semibold text-gray-700 uppercase">Bill To</h3>
              <p className="text-base font-medium mt-1">{invoice.customerName}</p>
              {invoice.customerGSTIN && (
                <p className="text-sm text-gray-600">GSTIN: {invoice.customerGSTIN}</p>
              )}
              {invoice.sendToEmail && (
                <p className="text-sm text-gray-600">{invoice.sendToEmail}</p>
              )}
            </div>

            {/* Items Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b-2 border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Item</th>
                    <th className="px-4 py-3 text-left font-semibold">HSN/SAC</th>
                    <th className="px-4 py-3 text-right font-semibold">Qty</th>
                    <th className="px-4 py-3 text-right font-semibold">Rate</th>
                    <th className="px-4 py-3 text-right font-semibold">GST %</th>
                    <th className="px-4 py-3 text-right font-semibold">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {invoice.items?.map((item, index) => (
                    <tr key={index}>
                      <td className="px-4 py-3">
                        <div className="font-medium">{item.itemName}</div>
                        {item.itemDescription && (
                          <div className="text-xs text-gray-500">{item.itemDescription}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">{item.hsnOrSac || item.hsnSac}</td>
                      <td className="px-4 py-3 text-right">{item.quantity}</td>
                      <td className="px-4 py-3 text-right">{formatCurrency(item.rate)}</td>
                      <td className="px-4 py-3 text-right">{item.gstPercent}%</td>
                      <td className="px-4 py-3 text-right">{formatCurrency(item.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="flex justify-end">
              <div className="w-64 space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Subtotal:</span>
                  <span className="font-medium">{formatCurrency(invoice.subTotal)}</span>
                </div>
                {invoice.gstType === 'IGST' && (
                  <div className="flex justify-between text-sm">
                    <span>IGST @ 18%:</span>
                    <span className="font-medium">{formatCurrency(invoice.igstAmount)}</span>
                  </div>
                )}
                {invoice.gstType === 'CGST_SGST' && (
                  <>
                    <div className="flex justify-between text-sm">
                      <span>CGST @ 9%:</span>
                      <span className="font-medium">{formatCurrency(invoice.cgstAmount)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>SGST @ 9%:</span>
                      <span className="font-medium">{formatCurrency(invoice.sgstAmount)}</span>
                    </div>
                  </>
                )}
                {invoice.tdsAmount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span>TDS Amount:</span>
                    <span className="font-medium text-red-600">-{formatCurrency(invoice.tdsAmount)}</span>
                  </div>
                )}
                {invoice.advanceReceived > 0 && (
                  <div className="flex justify-between text-sm">
                    <span>Advance Received:</span>
                    <span className="font-medium text-red-600">-{formatCurrency(invoice.advanceReceived)}</span>
                  </div>
                )}
                {dueDate && (
                  <div className="flex justify-between text-sm">
                    <span>Due Date:</span>
                    <span className="font-medium">{dueDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-bold border-t pt-2">
                  <span>Net Payable:</span>
                  <span>{formatCurrency(invoice.netPayable)}</span>
                </div>
              </div>
            </div>

            {/* Bank Details */}
            {(invoice.accountNo || invoice.ifscCode) && (
              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-sm font-semibold text-gray-700 uppercase mb-2">Bank Details</h3>
                <div className="text-sm space-y-1">
                  {invoice.bankName && <p><span className="font-medium">Bank:</span> {invoice.bankName}</p>}
                  {invoice.accountNo && <p><span className="font-medium">Account No:</span> {invoice.accountNo}</p>}
                  {invoice.ifscCode && <p><span className="font-medium">IFSC Code:</span> {invoice.ifscCode}</p>}
                  {invoice.accountName && <p><span className="font-medium">Account Name:</span> {invoice.accountName}</p>}
                </div>
              </div>
            )}

            {/* Payment Method */}
            {invoice.preferredPaymentMethod && (
              <div className="text-sm">
                <span className="font-medium">Payment Method:</span> {invoice.preferredPaymentMethod}
              </div>
            )}

            {/* UPI Details */}
            {invoice.upiId && (
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-700 uppercase mb-2">UPI Payment</h3>
                <div className="text-sm space-y-1">
                  <p><span className="font-medium">UPI ID:</span> {invoice.upiId}</p>
                  {invoice.upiNote && <p className="text-gray-600">{invoice.upiNote}</p>}
                </div>
              </div>
            )}

            {/* Payment Link */}
            {invoice.paymentLink && (
              <div className="text-sm">
                <span className="font-medium">Payment Link:</span>{' '}
                <a href={invoice.paymentLink} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  {invoice.paymentLink}
                </a>
              </div>
            )}

            {/* Notes */}
            {invoice.additionalNotes && (
              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-sm font-semibold text-gray-700 uppercase mb-2">Notes</h3>
                <p className="text-sm text-gray-600">{invoice.additionalNotes}</p>
              </div>
            )}

            {/* Payment Terms */}
            {invoice.paymentTerms && (
              <div className="text-sm text-gray-600">
                <span className="font-medium">Payment Terms:</span> {invoice.paymentTerms}
              </div>
            )}

            {/* Additional Fields */}
            {(invoice.ewayBillNumber || invoice.arnNumber || invoice.fssaiNumber || invoice.packingSlipNumber) && (
              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-sm font-semibold text-gray-700 uppercase mb-2">Additional Information</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {invoice.ewayBillNumber && (
                    <p><span className="font-medium">E-Way Bill:</span> {invoice.ewayBillNumber}</p>
                  )}
                  {invoice.arnNumber && (
                    <p><span className="font-medium">ARN:</span> {invoice.arnNumber}</p>
                  )}
                  {invoice.fssaiNumber && (
                    <p><span className="font-medium">FSSAI:</span> {invoice.fssaiNumber}</p>
                  )}
                  {invoice.packingSlipNumber && (
                    <p><span className="font-medium">Packing Slip:</span> {invoice.packingSlipNumber}</p>
                  )}
                </div>
              </div>
            )}

            {/* Signature */}
            {invoice.authorisedSignatoryName && (
              <div className="flex justify-end mt-8">
                <div className="text-right">
                  <div className="border-t border-gray-400 w-48 mb-2"></div>
                  <p className="font-medium">{invoice.authorisedSignatoryName}</p>
                  {invoice.designation && <p className="text-sm text-gray-600">{invoice.designation}</p>}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer Actions - Sticky */}
        <div className="flex justify-end gap-3 p-6 border-t flex-shrink-0 bg-white">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Close
          </button>
          <button
            onClick={handleDownloadPdf}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 flex items-center gap-2"
          >
            <Download size={16} />
            Download PDF
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvoicePreviewModal;